import React from 'react';
import { formatNumber } from '../../utils/constants';
import { getAccountIcon } from './helpers';

export const CashAccountsTab = ({ accountsSummary, cashAccountsTotal }) => {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Cash & Bank Accounts</h3>
      <p className="text-sm text-muted-foreground mb-4">
        {accountsSummary?.period_filtered
          ? 'Showing opening balance, period activity, and closing balance for the selected period.'
          : 'Cash accounts are automatically created when payments are recorded using different payment methods.'}
      </p>
      {accountsSummary?.period_filtered && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-slate-50 p-3 rounded-lg border">
            <p className="text-xs text-muted-foreground">Opening Balance</p>
            <p className="text-lg font-bold">{formatNumber(accountsSummary.total_opening)}</p>
          </div>
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <p className="text-xs text-green-700">Total Inflows</p>
            <p className="text-lg font-bold text-green-600">{formatNumber(accountsSummary.total_inflows)}</p>
          </div>
          <div className="bg-red-50 p-3 rounded-lg border border-red-200">
            <p className="text-xs text-red-700">Total Outflows</p>
            <p className="text-lg font-bold text-red-600">{formatNumber(accountsSummary.total_outflows)}</p>
          </div>
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700">Closing Balance</p>
            <p className="text-lg font-bold text-blue-600">{formatNumber(accountsSummary.total_closing)}</p>
          </div>
        </div>
      )}
      {accountsSummary?.cash_accounts?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="cash-accounts-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Account Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Method</th>
                {accountsSummary?.period_filtered && (
                  <>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Opening</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-green-600">Inflows</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-red-600">Outflows</th>
                  </>
                )}
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">
                  {accountsSummary?.period_filtered ? 'Closing' : 'Balance (RWF)'}
                </th>
              </tr>
            </thead>
            <tbody>
              {accountsSummary.cash_accounts.map(account => (
                <tr key={account.id || account.name} className="border-b border-border hover:bg-muted/30">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {getAccountIcon(account.name)}
                      <span className="font-medium">{account.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{account.payment_method || account.name}</td>
                  {accountsSummary?.period_filtered && (
                    <>
                      <td className="py-3 px-4 text-right">{formatNumber(account.opening_balance)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatNumber(account.period_inflows)}</td>
                      <td className="py-3 px-4 text-right text-red-600">{formatNumber(account.period_outflows)}</td>
                    </>
                  )}
                  <td className={`py-3 px-4 text-right font-semibold ${(account.closing_balance ?? account.balance) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatNumber(account.closing_balance ?? account.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted/30 font-semibold">
                <td colSpan={2} className="py-3 px-4">Total Cash</td>
                {accountsSummary?.period_filtered && (
                  <>
                    <td className="py-3 px-4 text-right">{formatNumber(accountsSummary.total_opening)}</td>
                    <td className="py-3 px-4 text-right text-green-600">{formatNumber(accountsSummary.total_inflows)}</td>
                    <td className="py-3 px-4 text-right text-red-600">{formatNumber(accountsSummary.total_outflows)}</td>
                  </>
                )}
                <td className="py-3 px-4 text-right text-emerald-600">RWF {formatNumber(cashAccountsTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <p className="text-center text-muted-foreground py-8">No cash accounts yet. Cash accounts are created automatically when payments are recorded.</p>
      )}
    </div>
  );
};
